import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import LeafletMap, { MapMarker, MapPolyline } from '../components/LeafletMap';
import { useToast } from '../components/Toast';
import { Vehicle, Depot, DeliveryPoint, RouteEntity } from '../types';
import { loadCostModel } from '../utils/costModel';
import { reverseGeocode, searchAddress } from '../utils/geocoding';
import { AddressItem } from '../utils/mockState';
import api from '../api/client';
import { fmtKm, fmtMin } from '../utils/mockState';
import './RouteNew.css?update=20260831';

// Tipo para os pontos desenhados no mapa
interface DrawnPoint extends MapMarker {
  address: string;
  weightKg: number;
  priority: 'normal' | 'alta' | 'media';
  timeWindowStart: string | null;
  timeWindowEnd: string | null;
}

// Tipo para o modo de entrada
type InputMode = 'search' | 'draw';

// Pontos criados nesta tela ainda não foram persistidos, portanto não possuem
// os campos de auditoria que a entidade DeliveryPoint recebe da API.
type RouteStopDraft = Omit<DeliveryPoint, 'userId' | 'createdAt' | 'updatedAt'> & {
  sequence?: number;
};

// A API responde erros de negócio em JSON ({ error }). Quando a resposta não
// tem esse formato — 404 em HTML de rota inexistente, 502 de proxy, servidor
// fora do ar — a mensagem genérica não dizia nada. Aqui o status vira parte do
// texto para que a falha seja diagnosticável sem abrir o DevTools.
function describeSaveError(err: any, isEditing: boolean): string {
  const apiError = err?.response?.data?.error;
  if (typeof apiError === 'string' && apiError) return apiError;

  const status = err?.response?.status;
  if (status === 404 && isEditing) {
    return 'Endpoint de edição não encontrado (404). Reinicie o servidor backend para carregar a versão mais recente.';
  }
  if (status) {
    return `Erro ${status} ao salvar rota. Verifique o servidor backend e tente novamente.`;
  }
  return 'Não foi possível falar com o servidor. Verifique se o backend está no ar e tente novamente.';
}

export default function RouteNew() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const costModel = loadCostModel();
  const [searchParams] = useSearchParams();

  // A mesma tela atende dois fluxos: criação (/rotas/nova) e edição de uma
  // rota ainda planejada (/rotas/:id/editar).
  const { id: routeId } = useParams<{ id: string }>();
  const isEditing = Boolean(routeId);

  // Estado da rota
  const [name, setName] = useState(`Rota — ${new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}`);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [vehicleId, setVehicleId] = useState('');
  const [driver, setDriver] = useState('');

  // Veículos e depósitos
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [depots, setDepots] = useState<Depot[]>([]);
  const [selectedDepotId, setSelectedDepotId] = useState('');
  const [startsAtDepot, setStartsAtDepot] = useState(true);
  const [endsAtDepot, setEndsAtDepot] = useState(true);

  // Modo de entrada (Busca ou Desenho no Mapa)
  // Inicia com 'draw' se a URL tiver ?mode=draw
  const [inputMode, setInputMode] = useState<InputMode>(
    searchParams.get('mode') === 'draw' ? 'draw' : 'search'
  );

  // Estado para busca por endereço
  const [searchAddr, setSearchAddr] = useState('');
  const [searchResults, setSearchResults] = useState<AddressItem[]>([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [activeSearchSuggestion, setActiveSearchSuggestion] = useState(-1);

  // Estado para desenho no mapa
  const [drawnPoints, setDrawnPoints] = useState<DrawnPoint[]>([]);
  const [drawnPolyline, setDrawnPolyline] = useState<MapPolyline | null>(null);
  const [drawMode, setDrawMode] = useState<'view' | 'draw'>('view');

  // Estado unificado dos pontos de parada (para ambos os modos)
  const [stops, setStops] = useState<RouteStopDraft[]>([]);

  // Janelas de tempo
  const [twModalOpen, setTwModalOpen] = useState(false);
  const [smartPasteModalOpen, setSmartPasteModalOpen] = useState(false);
  const [smartPasteText, setSmartPasteText] = useState('');
  const [smartPasteProcessing, setSmartPasteProcessing] = useState(false);
  const [smartPasteResults, setSmartPasteResults] = useState<{ address: string; success: boolean; error?: string }[]>([]);
  const [twStopIdx, setTwStopIdx] = useState<number | null>(null);
  const [twType, setTwType] = useState<'free' | 'custom'>('free');
  const [twStartH, setTwStartH] = useState(9);
  const [twStartM, setTwStartM] = useState(0);
  const [twEndH, setTwEndH] = useState(12);
  const [twEndM, setTwEndM] = useState(0);

  // Salvamento
  const [saving, setSaving] = useState(false);
  const isSavingRoute = useRef(false);
  const [geocoding, setGeocoding] = useState(false);

  // Drag and drop
  const [draggedIdx, setDraggedIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  // Carregar dados iniciais
  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [v, d] = await Promise.all([
          api.get<Vehicle[]>('/vehicles'),
          api.get<Depot[]>('/depots')
        ]);
        setVehicles(v.data);
        setDepots(d.data);
        if (v.data.length > 0) {
          setVehicleId(v.data[0].id);
          setDriver(v.data[0].driverName || '');
        }
        if (d.data.length > 0) {
          setSelectedDepotId(d.data[0].id);
        }

        if (routeId) {
          const { data: route } = await api.get<RouteEntity>(`/routes/${routeId}`);
          // Só o planejamento é editável. Depois de iniciada a rota vira
          // histórico operacional e reescrevê-la invalidaria as entregas já
          // registradas pelo motorista.
          if (route.status !== 'planejada') {
            showToast('Só é possível editar rotas com status "planejada". Duplique a rota para criar uma nova versão.', 'error');
            navigate(`/rotas/${routeId}`, { replace: true });
            return;
          }

          setName(route.name);
          setDate(new Date(route.date).toISOString().slice(0, 10));
          setVehicleId(route.vehicleId);
          setDriver(route.vehicle?.driverName || '');
          setSelectedDepotId(route.depotId);
          setStartsAtDepot(route.startsAtDepot);
          setEndsAtDepot(route.endsAtDepot);
          setStops(
            (route.stops || []).map((stop, index) => ({
              id: stop.deliveryPoint.id,
              title: stop.deliveryPoint.title,
              address: stop.deliveryPoint.address,
              latitude: stop.deliveryPoint.latitude,
              longitude: stop.deliveryPoint.longitude,
              weightKg: stop.weightKg,
              priority: (stop.priority as RouteStopDraft['priority']) || 'normal',
              timeWindowStart: stop.timeWindowStart,
              timeWindowEnd: stop.timeWindowEnd,
              sequence: index + 1
            }))
          );
        }
      } catch (err) {
        showToast(
          routeId ? 'Erro ao carregar a rota para edição.' : 'Erro ao carregar veículos e depósitos.',
          'error'
        );
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [showToast, routeId, navigate]);

  // Veículo selecionado
  const selectedVehicle = useMemo(() => {
    return vehicles.find((v) => v.id === vehicleId);
  }, [vehicles, vehicleId]);

  // Depósito selecionado
  const selectedDepot = useMemo(() => {
    return depots.find((d) => d.id === selectedDepotId);
  }, [depots, selectedDepotId]);

  // Busca por endereço
  const handleSearch = useCallback(async (query: string) => {
    setActiveSearchSuggestion(-1);
    if (!query.trim()) {
      setSearchResults([]);
      setShowSuggest(false);
      return;
    }
    setGeocoding(true);
    try {
      const results = await searchAddress(query);
      setSearchResults(results);
      setShowSuggest(results.length > 0);
    } catch (err) {
      showToast('Erro ao buscar endereço.', 'error');
    } finally {
      setGeocoding(false);
    }
  }, [showToast]);

  // Adicionar ponto por busca
  const handleAddFromSearch = (result: AddressItem) => {
    const now = new Date().toISOString();
    const newStop: DeliveryPoint = {
      id: `temp-${Date.now()}-${stops.length}`,
      title: `Parada ${stops.length + 1}`,
      address: result.label,
      latitude: result.lat,
      longitude: result.lng,
      weightKg: 50,
      priority: 'normal',
      timeWindowStart: null,
      timeWindowEnd: null,
      userId: '',
      createdAt: now,
      updatedAt: now
    };
    setStops([...stops, newStop]);
    setSearchAddr('');
    setSearchResults([]);
    setShowSuggest(false);
    setActiveSearchSuggestion(-1);
    showToast(`Ponto adicionado: ${result.label}`, 'success');
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setShowSuggest(false);
      setActiveSearchSuggestion(-1);
      return;
    }
    if (!showSuggest || searchResults.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveSearchSuggestion((index) => Math.min(index + 1, searchResults.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveSearchSuggestion((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter' && activeSearchSuggestion >= 0 && activeSearchSuggestion < searchResults.length) {
      event.preventDefault();
      handleAddFromSearch(searchResults[activeSearchSuggestion]);
    }
  };

  // Adicionar ponto por clique no mapa
  const handleMapClick = async (lat: number, lng: number) => {
    if (drawMode !== 'draw') return;

    setGeocoding(true);
    try {
      const addressResult = await reverseGeocode(lat, lng);
      const addressLabel = addressResult?.label || `Localização ${lat.toFixed(4)}, ${lng.toFixed(4)}`;
      const newPoint: DrawnPoint = {
        lat,
        lng,
        address: addressLabel,
        weightKg: 50,
        priority: 'normal',
        timeWindowStart: null,
        timeWindowEnd: null
      };
      setDrawnPoints([...drawnPoints, newPoint]);

      // Também adiciona ao stops unificado
      const now = new Date().toISOString();
      const newStop: DeliveryPoint = {
        id: `temp-${Date.now()}-${stops.length}`,
        title: `Parada ${stops.length + 1}`,
        address: addressLabel,
        latitude: lat,
        longitude: lng,
        weightKg: 50,
        priority: 'normal',
        timeWindowStart: null,
        timeWindowEnd: null,
        userId: '',
        createdAt: now,
        updatedAt: now
      };
      setStops([...stops, newStop]);

      showToast(`Ponto adicionado no mapa: ${addressLabel}`, 'success');
    } catch (err) {
      showToast('Erro ao obter endereço para esta localização.', 'error');
    } finally {
      setGeocoding(false);
    }
  };

  // Remover último ponto (desenho no mapa)
  const removeLastPoint = () => {
    if (drawnPoints.length === 0) return;
    const newPoints = drawnPoints.slice(0, -1);
    setDrawnPoints(newPoints);
    // Também remove do stops
    setStops(stops.slice(0, -1));
  };

  // Limpar todos os pontos (desenho no mapa)
  const clearAllPoints = () => {
    setDrawnPoints([]);
    setDrawnPolyline(null);
    setStops([]);
  };

  // Atualizar endereço de um ponto
  const handleUpdateAddress = (idx: number, address: string) => {
    setStops(stops.map((s, i) => (i === idx ? { ...s, address } : s)));
  };

  // Atualizar peso de um ponto
  const handleUpdateWeight = (idx: number, weightKg: number) => {
    setStops(stops.map((s, i) => (i === idx ? { ...s, weightKg } : s)));
  };

  // Atualizar prioridade de um ponto
  const handleUpdatePriority = async (idx: number, priority: 'normal' | 'alta' | 'media') => {
    setStops(stops.map((s, i) => (i === idx ? { ...s, priority } : s)));
  };

  // Janelas de tempo
  const openTwModal = (idx: number) => {
    const s = stops[idx];
    setTwStopIdx(idx);
    if (s.timeWindowStart && s.timeWindowEnd) {
      setTwType('custom');
      const [sh, sm] = s.timeWindowStart.split(':').map((n) => parseInt(n, 10) || 0);
      const [eh, em] = s.timeWindowEnd.split(':').map((n) => parseInt(n, 10) || 0);
      setTwStartH(sh);
      setTwStartM(sm);
      setTwEndH(eh);
      setTwEndM(em);
    } else {
      setTwType('free');
      setTwStartH(9);
      setTwStartM(0);
      setTwEndH(12);
      setTwEndM(0);
    }
    setTwModalOpen(true);
  };

  const applyPreset = (sh: number, sm: number, eh: number, em: number) => {
    setTwType('custom');
    setTwStartH(sh);
    setTwStartM(sm);
    setTwEndH(eh);
    setTwEndM(em);
  };

  const saveTimeWindow = () => {
    if (twStopIdx === null) return;
    const timeWindowStart = twType === 'custom' ? `${String(twStartH).padStart(2, '0')}:${String(twStartM).padStart(2, '0')}` : null;
    const timeWindowEnd = twType === 'custom' ? `${String(twEndH).padStart(2, '0')}:${String(twEndM).padStart(2, '0')}` : null;
    setStops(stops.map((s, i) => (i === twStopIdx ? { ...s, timeWindowStart, timeWindowEnd } : s)));
    setTwModalOpen(false);
  };

  // Remover ponto
  const handleRemoveStop = (idx: number) => {
    const newStops = stops.filter((_, i) => i !== idx);
    setStops(newStops);
    // Atualizar sequencia
    setStops(newStops.map((s, i) => ({ ...s, sequence: i + 1 })));
  };

  // Métricas de pré-visualização
  const previewMetrics = useMemo(() => {
    if (stops.length < 2 || !selectedVehicle) {
      return { distance: 0, duration: 0, fuel: 0 };
    }

    // Cálculo simples para pré-visualização (será recalculado pelo OR-Tools ao salvar)
    let totalDistance = 0;
    let totalDuration = 0;

    for (let i = 0; i < stops.length - 1; i++) {
      const a = stops[i];
      const b = stops[i + 1];
      // Distância aproximada (Haversine)
      const lat1 = a.latitude;
      const lon1 = a.longitude;
      const lat2 = b.latitude;
      const lon2 = b.longitude;
      const R = 6371; // km
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLon = (lon2 - lon1) * Math.PI / 180;
      const aH = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(aH), Math.sqrt(1 - aH));
      const distance = R * c;
      totalDistance += distance;
      // Tempo aproximado (60 km/h para entrega urbana)
      totalDuration += distance / 60 * 60; // em minutos
    }

    // Combustível (baseado no consumo do veículo)
    const fuelConsumption = 0.08; // consumo padrão: 0.08 L/km
    const fuel = totalDistance * fuelConsumption;

    return {
      distance: totalDistance,
      duration: totalDuration,
      fuel
    };
  }, [stops, selectedVehicle]);

  // Salvar rota
  const handleSaveRoute = async () => {
  if (isSavingRoute.current) return;

  if (stops.length < 2) {
    showToast('Adicione pelo menos 2 pontos de parada para criar uma rota.', 'error');
    return;
  }

  if (!vehicleId || !selectedDepotId) {
    showToast('Selecione o veículo e o depósito de origem.', 'error');
    return;
  }

  isSavingRoute.current = true;
  setSaving(true);
  try {
    const payload = {
      name,
      date,
      vehicleId,
      driver,
      depotId: selectedDepotId,
      startsAtDepot,
      endsAtDepot,
      points: stops.map((stop) => ({
        lat: stop.latitude,
        lng: stop.longitude,
        address: stop.address,
        weightKg: stop.weightKg,
        priority: stop.priority,
        timeWindowStart: stop.timeWindowStart,
        timeWindowEnd: stop.timeWindowEnd
      }))
    };

    const { data } = isEditing
      ? await api.put<RouteEntity>(`/routes/${routeId}`, payload)
      : await api.post<RouteEntity>('/routes', payload);

    showToast(
      isEditing
        ? `Rota "${name}" atualizada e reotimizada com sucesso!`
        : `Rota "${name}" criada e otimizada com sucesso!`,
      'success'
    );
    navigate(`/rotas/${data.id}`);
  } catch (err: any) {
    showToast(describeSaveError(err, isEditing), 'error');
  } finally {
    isSavingRoute.current = false;
    setSaving(false);
  }
  return;

  /* Fluxo legado duplicado, mantido temporariamente como referência durante
     a migração da tela. O salvamento acima é o único caminho executado. */
  /*
  if (smartPasteResults.length > 0) {
    const successfulPoints = smartPasteResults.filter((r) => r.success);
    if (successfulPoints.length > 0) {
      const newStops: RouteStopDraft[] = successfulPoints.map((r, i) => ({
        id: `smart-paste-${i}`,
        title: r.address,
        address: r.address,
        latitude: 0,
        longitude: 0,
        weightKg: 1,
        priority: 'normal',
        timeWindowStart: null,
        timeWindowEnd: null,
        sequence: stops.length + i + 1,
      }));
      setStops([...stops, ...newStops]);
    }
  }

  if (stops.length < 2 || !vehicleId) {
    showToast('Adicione pelo menos 2 pontos de parada e selecione um veículo.', 'error');
    return;
  }

  setSaving(true);
  try {
    const { data } = await api.post('/optimize', {
      pointIds: stops.map((s) => s.id),
      vehicleId,
      name,
      date: new Date(date).toISOString(),
    });
    showToast('Rota criada e otimizada com sucesso!', 'success');
    navigate(`/rotas/${data.route.id}`);
  } catch (error: any) {
    showToast('Erro ao criar rota: ' + (error instanceof Error ? error.message : 'Erro desconhecido'), 'error');
  } finally {
    setSaving(false);
  }
  if (smartPasteResults.length > 0) {
    const successfulPoints = smartPasteResults.filter((r) => r.success);
    if (successfulPoints.length > 0) {
      const newStops: RouteStopDraft[] = successfulPoints.map((r, i) => ({
        id: `smart-paste-${i}`,
        title: r.address,
        address: r.address,
        latitude: 0,
        longitude: 0,
        weightKg: 1,
        priority: 'normal',
        timeWindowStart: null,
        timeWindowEnd: null,
        sequence: stops.length + i + 1,
      }));
      setStops([...stops, ...newStops]);
    }
  }

  if (stops.length < 2 || !vehicleId) {
    showToast('Adicione pelo menos 2 pontos de parada e selecione um veículo.', 'error');
    return;
  }

  setSaving(true);
  try {
    const { data } = await api.post('/optimize', {
      pointIds: stops.map((s) => s.id),
      vehicleId,
      name,
      date: new Date(date).toISOString(),
    });
    showToast('Rota criada e otimizada com sucesso!', 'success');
    navigate(`/rotas/${data.route.id}`);
  } catch (error: any) {
    showToast('Erro ao criar rota: ' + (error instanceof Error ? error.message : 'Erro desconhecido'), 'error');
  } finally {
    setSaving(false);
  }
  if (smartPasteResults.length > 0) {
    const successfulPoints = smartPasteResults.filter((r) => r.success);
    if (successfulPoints.length > 0) {
      const newStops: RouteStopDraft[] = successfulPoints.map((r, i) => ({
        id: `smart-paste-${i}`,
        title: r.address,
        address: r.address,
        latitude: 0,
        longitude: 0,
        weightKg: 1,
        priority: 'normal',
        timeWindowStart: null,
        timeWindowEnd: null,
        sequence: stops.length + i + 1,
      }));
      setStops([...stops, ...newStops]);
    }
  }

  if (stops.length < 2 || !vehicleId) {
    showToast('Adicione pelo menos 2 pontos de parada e selecione um veículo.', 'error');
    return;
  }

  setSaving(true);
  try {
    const { data } = await api.post('/optimize', {
      pointIds: stops.map((s) => s.id),
      vehicleId,
      name,
      date: new Date(date).toISOString(),
    });
    showToast('Rota criada e otimizada com sucesso!', 'success');
    navigate(`/rotas/${data.route.id}`);
  } catch (error: any) {
    showToast('Erro ao criar rota: ' + (error instanceof Error ? error.message : 'Erro desconhecido'), 'error');
  } finally {
    setSaving(false);
  }
  if (smartPasteResults.length > 0) {
    const successfulPoints = smartPasteResults.filter((r) => r.success);
    if (successfulPoints.length > 0) {
      const newStops: RouteStopDraft[] = successfulPoints.map((r, i) => ({
        id: `smart-paste-${i}`,
        title: r.address,
        address: r.address,
        latitude: 0,
        longitude: 0,
        weightKg: 1,
        priority: 'normal',
        timeWindowStart: null,
        timeWindowEnd: null,
        sequence: stops.length + i + 1,
      }));
      setStops([...stops, ...newStops]);
    }
  }

  if (stops.length < 2) {
    showToast('Adicione pelo menos 2 pontos de parada para criar uma rota.', 'error');
    return;
  }

  if (!vehicleId) {
    showToast('Selecione um veículo para a rota.', 'error');
    return;
  }

  setSaving(true);
  try {
    const { data } = await api.post('/optimize', {
      pointIds: stops.map((s) => s.id),
      vehicleId,
      name,
      date: new Date(date).toISOString(),
    });
    showToast('Rota criada e otimizada com sucesso!', 'success');
    navigate(`/rotas/${data.route.id}`);
  } catch (error: any) {
    showToast('Erro ao criar rota: ' + (error instanceof Error ? error.message : 'Erro desconhecido'), 'error');
  } finally {
    setSaving(false);
  }
  if (smartPasteResults.length > 0) {
    const successfulPoints = smartPasteResults.filter((r) => r.success);
    if (successfulPoints.length > 0) {
      const newStops: RouteStopDraft[] = successfulPoints.map((r, i) => ({
        id: `smart-paste-${i}`,
        title: r.address,
        address: r.address,
        latitude: 0,
        longitude: 0,
        weightKg: 1,
        priority: 'normal',
        timeWindowStart: null,
        timeWindowEnd: null,
        sequence: stops.length + i + 1,
      }));
      setStops([...stops, ...newStops]);
    }
  }

  if (stops.length < 2) {
    showToast('Adicione pelo menos 2 pontos de parada para criar uma rota.', 'error');
    return;
  }

  if (!vehicleId) {
    showToast('Selecione um veículo para a rota.', 'error');
    return;
  }

  setSaving(true);
  try {
    const { data } = await api.post('/optimize', {
      pointIds: stops.map((s) => s.id),
      vehicleId,
      name,
      date: new Date(date).toISOString(),
    });
    showToast('Rota criada e otimizada com sucesso!', 'success');
    navigate(`/rotas/${data.route.id}`);
  } catch (error: any) {
    showToast('Erro ao criar rota: ' + (error instanceof Error ? error.message : 'Erro desconhecido'), 'error');
  } finally {
    setSaving(false);
  }
  if (smartPasteResults.length > 0) {
    const successfulPoints = smartPasteResults.filter((r) => r.success);
    if (successfulPoints.length > 0) {
      const newStops: RouteStopDraft[] = successfulPoints.map((r, i) => ({
        id: `smart-paste-${i}`,
        title: r.address,
        address: r.address,
        latitude: 0,
        longitude: 0,
        weightKg: 1,
        priority: 'normal',
        timeWindowStart: null,
        timeWindowEnd: null,
        sequence: stops.length + i + 1,
      }));
      setStops([...stops, ...newStops]);
    }
  }
    if (stops.length < 2 || !vehicleId) {
      showToast('Adicione pelo menos 2 pontos de parada e selecione um veículo.', 'error');
      return;
    }

    setSaving(true);
    try {
      // Preparar dados para a API
      const routeData = {
        name,
        date,
        vehicleId,
        driver,
        depotId: selectedDepotId,
        points: stops.map((s) => ({
          lat: s.latitude,
          lng: s.longitude,
          address: s.address,
          weightKg: s.weightKg,
          priority: s.priority,
          timeWindowStart: s.timeWindowStart,
          timeWindowEnd: s.timeWindowEnd
        }))
      };

      // Chamar API para criar e otimizar rota
      const response = await api.post<RouteEntity>('/routes', routeData);

      showToast(`Rota "${name}" criada e otimizada com sucesso!`, 'success');
      navigate(`/rotas/${response.data.id}`);
    } catch (err) {
      showToast('Erro ao salvar rota. Tente novamente.', 'error');
    } finally {
      setSaving(false);
    }
  */
  };

  // Alternar modo de entrada
  const toggleInputMode = (mode: InputMode) => {
    setInputMode(mode);
    // Se trocando para desenho, ativa o modo de desenho no mapa
    if (mode === 'draw') {
      setDrawMode('draw');
    } else {
      setDrawMode('view');
    }
  };

  // Marcadores para o mapa (baseado nos stops)
  const mapMarkers: MapMarker[] = useMemo(() => {
    const depotMarker = selectedDepot && (startsAtDepot || endsAtDepot)
      ? [{ lat: selectedDepot.latitude, lng: selectedDepot.longitude, label: 'CD', color: '#0f4c5c' }]
      : [];
    return [
      ...depotMarker,
      ...stops.map((stop, index) => ({
        lat: stop.latitude,
        lng: stop.longitude,
        label: index + 1,
        color: '#2a9d8f'
      }))
    ];
  }, [stops, selectedDepot, startsAtDepot, endsAtDepot]);

  // Polyline para o mapa
  const mapPolyline: MapPolyline | null = useMemo(() => {
    const positions: [number, number][] = [
      ...(startsAtDepot && selectedDepot ? [[selectedDepot.latitude, selectedDepot.longitude] as [number, number]] : []),
      ...stops.map((stop) => [stop.latitude, stop.longitude] as [number, number]),
      ...(endsAtDepot && selectedDepot ? [[selectedDepot.latitude, selectedDepot.longitude] as [number, number]] : [])
    ];
    if (positions.length < 2) return null;
    return {
      positions,
      color: '#2a9d8f'
    };
  }, [stops, selectedDepot, startsAtDepot, endsAtDepot]);

  // Cores das prioridades
  const priorityColors = {
    normal: '#2a9d8f',
    alta: '#f59e0b',
    urgente: '#ef4444'
  };

  if (loading) {
    return (
      <Layout>
        <div style={{ padding: 40, textAlign: 'center' }}>
          <div className="spinner" />
          <p>{isEditing ? 'Carregando rota para edição...' : 'Carregando veículos e depósitos...'}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="route-new-container">
        <div className="page-header">
          <h1>{isEditing ? 'Editar Rota' : 'Nova Rota'}</h1>
          <p className="page-subtitle">
            {isEditing
              ? 'Ajuste paradas, veículo, CD e data. Ao salvar, a rota é reotimizada com o novo planejamento.'
              : 'Crie uma nova rota de entrega de forma simples e intuitiva'}
          </p>
        </div>

        {/* Seletor de Modo */}
        <div className="input-mode-tabs">
          <button
            type="button"
            className={`mode-tab ${inputMode === 'search' ? 'active' : ''}`}
            onClick={() => toggleInputMode('search')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            Busca por Endereço
          </button>
          <button
            type="button"
            className={`mode-tab ${inputMode === 'draw' ? 'active' : ''}`}
            onClick={() => toggleInputMode('draw')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18" height="18">
              <path d="M9 17l3-3-3-3" />
              <path d="M15 17l-3-3 3-3" />
              <path d="M9 3v18" />
              <path d="M15 3v18" />
              <path d="M3 9h18" />
              <path d="M3 15h18" />
            </svg>
            Desenhar no Mapa
          </button>
        </div>

        <div className="route-new-content">
          {/* Painel Esquerdo - Configurações da Rota */}
          <div className="left-panel">
            {/* Configurações Básicas */}
            <div className="form-section">
              <h3>Configurações da Rota</h3>

              <div className="field">
                <label>Nome da Rota</label>
                <input
                  type="text"
                  className="input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex: Rota Manhã - São Paulo"
                />
              </div>

              <div className="field">
                <label>Data</label>
                <input
                  type="date"
                  className="input"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>

              <div className="field">
                <label>Veículo</label>
                <select
                  className="input"
                  value={vehicleId}
                  onChange={(e) => {
                    const v = vehicles.find((ve) => ve.id === e.target.value);
                    setVehicleId(e.target.value);
                    setDriver(v?.driverName || '');
                  }}
                >
                  {vehicles.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name} ({v.capacityKg}kg)
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label>Motorista</label>
                <input
                  type="text"
                  className="input"
                  value={driver}
                  onChange={(e) => setDriver(e.target.value)}
                  placeholder="Nome do motorista"
                />
              </div>

              <div className="field">
                <label>Depósito de Origem</label>
                <select
                  className="input"
                  value={selectedDepotId}
                  onChange={(e) => setSelectedDepotId(e.target.value)}
                >
                  {depots.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name} - {d.address}
                    </option>
                  ))}
                </select>
              </div>

              <div className="route-depot-options">
                <h4>Origem e chegada da rota</h4>
                <p>Defina separadamente se o CD selecionado deve ser incluído no início e no fim do trajeto.</p>

                <fieldset>
                  <legend>A rota começa no CD selecionado?</legend>
                  <label>
                    <input type="radio" name="starts-at-depot" checked={startsAtDepot} onChange={() => setStartsAtDepot(true)} />
                    Sim, partir do CD
                  </label>
                  <label>
                    <input type="radio" name="starts-at-depot" checked={!startsAtDepot} onChange={() => setStartsAtDepot(false)} />
                    Não, iniciar no primeiro endereço
                  </label>
                </fieldset>

                <fieldset>
                  <legend>A rota termina no CD selecionado?</legend>
                  <label>
                    <input type="radio" name="ends-at-depot" checked={endsAtDepot} onChange={() => setEndsAtDepot(true)} />
                    Sim, retornar ao CD
                  </label>
                  <label>
                    <input type="radio" name="ends-at-depot" checked={!endsAtDepot} onChange={() => setEndsAtDepot(false)} />
                    Não, terminar no último endereço
                  </label>
                </fieldset>
              </div>
            </div>

            {/* Modo Busca por Endereço */}
            {inputMode === 'search' && (
              <div className="form-section">
                <h3>Adicionar Endereços</h3>
                <div className="field" style={{ position: 'relative', marginBottom: 20 }}>
                  <label>Buscar endereço</label>
                  <div className="input-wrap">
                    <svg className="icn" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="11" cy="11" r="8" />
                      <path d="m21 21-4.35-4.35" />
                    </svg>
                    <input
                      type="text"
                      className="input"
                      value={searchAddr}
                      onChange={(e) => {
                        setSearchAddr(e.target.value);
                        handleSearch(e.target.value);
                      }}
                      onKeyDown={handleSearchKeyDown}
                      placeholder="Digite um endereço para buscar..."
                      role="combobox"
                      aria-expanded={showSuggest}
                      aria-controls="new-route-address-suggestions"
                      aria-activedescendant={activeSearchSuggestion >= 0 ? `new-route-address-option-${activeSearchSuggestion}` : undefined}
                    />
                    {geocoding && <div className="input-loading" />}
                  </div>
                  {showSuggest && searchResults.length > 0 && (
                    <ul id="new-route-address-suggestions" className="suggestions-list" role="listbox">
                      {searchResults.map((r, idx) => (
                        <li
                          key={idx}
                          onClick={() => handleAddFromSearch(r)}
                          id={`new-route-address-option-${idx}`}
                          className={`suggestion-item ${activeSearchSuggestion === idx ? 'keyboard-active' : ''}`}
                          role="option"
                          aria-selected={activeSearchSuggestion === idx}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                            <circle cx="12" cy="10" r="3" />
                          </svg>
                          <span>{r.label}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}

            {/* Modo Desenhar no Mapa */}
            {inputMode === 'draw' && (
              <div className="form-section">
                <h3>Desenhar no Mapa</h3>
                <p style={{ fontSize: 14, color: 'var(--text-3)', marginBottom: 16 }}>
                  Clique no mapa para adicionar pontos de parada. O endereço será obtido automaticamente.
                </p>
                <div className="draw-controls">
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={removeLastPoint}
                    disabled={stops.length === 0}
                    title="Desfazer último ponto"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18" />
                      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                    </svg>
                    Desfazer
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={clearAllPoints}
                    disabled={stops.length === 0}
                    title="Limpar todos os pontos"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18" />
                      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                      <path d="M12 12v6" />
                      <path d="M10 14h4" />
                    </svg>
                    Limpar Tudo
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSaveRoute}
                    disabled={stops.length < 2 || saving || !vehicleId}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                    </svg>
                    {isEditing ? 'Salvar Alterações' : 'Salvar Rota Desenhada'}
                  </button>
                </div>
              </div>
            )}

            {/* Lista de Pontos de Parada (comum para ambos os modos) */}
            {stops.length > 0 && (
              <div className="form-section">
                <h3>Pontos de Parada ({stops.length})</h3>
                <p style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 12 }}>
                  Arraste para reordenar. Clique no número para editar detalhes.
                </p>
                <div className="points-scroll" style={{ maxHeight: 300 }}>
                  {stops.map((point, idx) => (
                    <div
                      key={point.id}
                      className="drawn-point-card"
                      draggable
                      onDragStart={() => setDraggedIdx(idx)}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragOverIdx(idx);
                      }}
                      onDragLeave={() => setDragOverIdx(null)}
                      onDrop={() => {
                        if (draggedIdx !== null && draggedIdx !== idx) {
                          const newStops = [...stops];
                          const [removed] = newStops.splice(draggedIdx, 1);
                          newStops.splice(idx, 0, removed);
                          setStops(newStops.map((s, i) => ({ ...s, sequence: i + 1 })));
                        }
                        setDragOverIdx(null);
                      }}
                      onDragEnd={() => {
                        setDraggedIdx(null);
                        setDragOverIdx(null);
                      }}
                      style={{
                        marginBottom: 12,
                        padding: 12,
                        background: 'var(--bg-subtle)',
                        borderRadius: 8,
                        border: '1px solid var(--border)',
                        opacity: dragOverIdx === idx ? 0.8 : 1,
                        cursor: 'grab'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span
                          className="point-num"
                          onClick={() => openTwModal(idx)}
                          style={{ cursor: 'pointer' }}
                        >
                          {idx + 1}
                        </span>
                        <span className="point-coords mono" style={{ fontSize: 11, color: 'var(--text-4)' }}>
                          {point.latitude.toFixed(5)}, {point.longitude.toFixed(5)}
                        </span>
                      </div>
                      <div className="field" style={{ marginBottom: 8 }}>
                        <label style={{ fontSize: 11, fontWeight: 600 }}>Endereço (Editável)</label>
                        <input
                          type="text"
                          className="input"
                          value={point.address}
                          onChange={(e) => handleUpdateAddress(idx, e.target.value)}
                          placeholder="Digite o endereço correto da entrega"
                        />
                      </div>
                      <div style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
                        <div className="field" style={{ flex: 1 }}>
                          <label style={{ fontSize: 11, fontWeight: 600 }}>Peso (kg)</label>
                          <input
                            type="number"
                            className="input"
                            value={point.weightKg}
                            onChange={(e) => handleUpdateWeight(idx, Number(e.target.value))}
                            min={1}
                          />
                        </div>
                        <div className="field" style={{ flex: 1 }}>
                          <label style={{ fontSize: 11, fontWeight: 600 }}>Prioridade</label>
                          <select
                            className="input"
                            value={point.priority}
                            onChange={(e) => handleUpdatePriority(idx, e.target.value as 'normal' | 'alta' | 'media')}
                          >
                            <option value="normal">Normal</option>
                            <option value="alta">Alta</option>
                            <option value="media">Urgente</option>
                          </select>
                        </div>
                      </div>
                      {point.timeWindowStart && point.timeWindowEnd && (
                        <div style={{ fontSize: 11, color: 'var(--text-4)' }}>
                          Janela: {point.timeWindowStart} - {point.timeWindowEnd}
                        </div>
                      )}
                      <button
                        type="button"
                        className="btn btn-danger btn-xs"
                        onClick={() => handleRemoveStop(idx)}
                        style={{ marginTop: 8 }}
                      >
                        Remover
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Botão de Salvar */}
            <div style={{ marginTop: 20 }}>
              <button
                className="btn btn-primary btn-block btn-lg"
                onClick={handleSaveRoute}
                disabled={stops.length < 2 || saving || !vehicleId}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                </svg>
                {saving
                  ? isEditing
                    ? 'Salvando e reotimizando...'
                    : 'Criando e otimizando rota...'
                  : isEditing
                  ? 'Salvar Alterações e Reotimizar'
                  : 'Criar e Otimizar Rota'}
              </button>
            </div>

            <div style={{ marginTop: 12 }}>
              <button
                className="btn btn-secondary btn-block"
                onClick={() => navigate(isEditing ? `/rotas/${routeId}` : '/rotas')}
              >
                {isEditing ? 'Cancelar Edição' : 'Cancelar e Voltar'}
              </button>
            </div>
          </div>

          {/* Painel Direito - Mapa e Resumo */}
          <div className="right-panel">
            {/* Mapa */}
            <div className="map-container">
              <LeafletMap
                markers={mapMarkers}
                polylines={mapPolyline ? [mapPolyline] : []}
                height="100%"
                defaultColor="#2a9d8f"
                interactive={inputMode === 'draw'}
                onMapClick={handleMapClick}
              />
            </div>

            {/* Resumo da Rota */}
            <div className="route-summary">
              <div className="summary-card">
                <h4>Resumo da Rota</h4>

                <div className="summary-metric">
                  <span className="k">Pontos de parada</span>
                  <span className="v mono">{stops.length} <span className="u">pontos</span></span>
                </div>

                <div className="summary-metric">
                  <span className="k">Distância estimada</span>
                  <span className="v mono">{fmtKm(previewMetrics.distance)} <span className="u">km</span></span>
                </div>

                <div className="summary-metric">
                  <span className="k">Duração estimada</span>
                  <span className="v mono">{fmtMin(previewMetrics.duration)}</span>
                </div>

                <div className="summary-metric">
                  <span className="k">Combustível estimado</span>
                  <span className="v mono">~{previewMetrics.fuel.toFixed(1)} <span className="u">litros</span></span>
                </div>

                {selectedVehicle && (
                  <div className="summary-metric">
                    <span className="k">Capacidade do veículo</span>
                    <span className="v mono">{selectedVehicle.capacityKg} <span className="u">kg</span></span>
                  </div>
                )}

                <p style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 8 }}>
                  Números finais são calculados pelo motor OR-Tools ao salvar.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Modal de Janela de Horário */}
        <div
          className="modal-overlay"
          style={{ display: twModalOpen ? 'flex' : 'none', alignItems: 'center', justifyContent: 'center', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000 }}
          onClick={() => setTwModalOpen(false)}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: 12, padding: 24, width: '90%', maxWidth: 480, maxHeight: '80vh', overflow: 'auto' }}
          >
            <h3 style={{ margin: '0 0 20px', fontSize: 18 }}>Janela de Horário da Parada {twStopIdx !== null ? twStopIdx + 1 : ''}</h3>

            <div className="tw-toggle-row" style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
              <button
                className={twType === 'free' ? 'on' : ''}
                onClick={() => setTwType('free')}
                style={{ flex: 1, padding: 10, border: '1px solid var(--border)', background: twType === 'free' ? 'var(--brand)' : 'white', color: twType === 'free' ? 'white' : 'var(--text-1)', borderRadius: 6, cursor: 'pointer' }}
              >
                Sem restrição (Livre)
              </button>
              <button
                className={twType === 'custom' ? 'on' : ''}
                onClick={() => setTwType('custom')}
                style={{ flex: 1, padding: 10, border: '1px solid var(--border)', background: twType === 'custom' ? 'var(--brand)' : 'white', color: twType === 'custom' ? 'white' : 'var(--text-1)', borderRadius: 6, cursor: 'pointer' }}
              >
                Janela específica
              </button>
            </div>

            <div className={`tw-picker ${twType === 'free' ? 'disabled' : ''}`} style={{ opacity: twType === 'free' ? 0.5 : 1 }}>
              <div className="time-stepper" style={{ marginBottom: 16 }}>
                <div className="ts-label" style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Início da janela</div>
                <div className="ts-body" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div className="ts-unit" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <button type="button" className="ts-arrow" onClick={() => setTwStartH((h) => (h + 1) % 24)} disabled={twType === 'free'}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="18 15 12 9 6 15"></polyline></svg>
                    </button>
                    <div className="ts-value mono" style={{ minWidth: 40, textAlign: 'center', fontFamily: 'monospace' }}>{String(twStartH).padStart(2, '0')}</div>
                    <button type="button" className="ts-arrow" onClick={() => setTwStartH((h) => (h - 1 + 24) % 24)} disabled={twType === 'free'}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="6 9 12 15 18 9"></polyline></svg>
                    </button>
                  </div>
                  <div className="ts-sep">:</div>
                  <div className="ts-unit" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <button type="button" className="ts-arrow" onClick={() => setTwStartM((m) => (m + 15) % 60)} disabled={twType === 'free'}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="18 15 12 9 6 15"></polyline></svg>
                    </button>
                    <div className="ts-value mono" style={{ minWidth: 40, textAlign: 'center', fontFamily: 'monospace' }}>{String(twStartM).padStart(2, '0')}</div>
                    <button type="button" className="ts-arrow" onClick={() => setTwStartM((m) => (m - 15 + 60) % 60)} disabled={twType === 'free'}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="6 9 12 15 18 9"></polyline></svg>
                    </button>
                  </div>
                </div>
              </div>

              <div className="tw-arrow-connector" style={{ textAlign: 'center', margin: '8px 0', color: 'var(--text-3)' }}>➔</div>

              <div className="time-stepper">
                <div className="ts-label" style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Fim da janela</div>
                <div className="ts-body" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div className="ts-unit" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <button type="button" className="ts-arrow" onClick={() => setTwEndH((h) => (h + 1) % 24)} disabled={twType === 'free'}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="18 15 12 9 6 15"></polyline></svg>
                    </button>
                    <div className="ts-value mono" style={{ minWidth: 40, textAlign: 'center', fontFamily: 'monospace' }}>{String(twEndH).padStart(2, '0')}</div>
                    <button type="button" className="ts-arrow" onClick={() => setTwEndH((h) => (h - 1 + 24) % 24)} disabled={twType === 'free'}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="6 9 12 15 18 9"></polyline></svg>
                    </button>
                  </div>
                  <div className="ts-sep">:</div>
                  <div className="ts-unit" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <button type="button" className="ts-arrow" onClick={() => setTwEndM((m) => (m + 15) % 60)} disabled={twType === 'free'}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="18 15 12 9 6 15"></polyline></svg>
                    </button>
                    <div className="ts-value mono" style={{ minWidth: 40, textAlign: 'center', fontFamily: 'monospace' }}>{String(twEndM).padStart(2, '0')}</div>
                    <button type="button" className="ts-arrow" onClick={() => setTwEndM((m) => (m - 15 + 60) % 60)} disabled={twType === 'free'}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="6 9 12 15 18 9"></polyline></svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="tw-quick" style={{ marginTop: 16 }}>
              <div className="tw-quick-lbl" style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Presets rápidos:</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                <button type="button" className="tw-preset" onClick={() => applyPreset(8, 0, 12, 0)} disabled={twType === 'free'} style={{ padding: '6px 12px', background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>08h–12h (Manhã)</button>
                <button type="button" className="tw-preset" onClick={() => applyPreset(13, 0, 17, 0)} disabled={twType === 'free'} style={{ padding: '6px 12px', background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>13h–17h (Tarde)</button>
                <button type="button" className="tw-preset" onClick={() => applyPreset(8, 0, 10, 0)} disabled={twType === 'free'} style={{ padding: '6px 12px', background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>08h–10h</button>
                <button type="button" className="tw-preset" onClick={() => applyPreset(14, 0, 18, 0)} disabled={twType === 'free'} style={{ padding: '6px 12px', background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>14h–18h</button>
                <button type="button" className="tw-preset" onClick={() => applyPreset(9, 0, 11, 0)} disabled={twType === 'free'} style={{ padding: '6px 12px', background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>09h–11h</button>
              </div>
            </div>

            <div className={`tw-preview ${twType === 'free' ? 'free' : ''}`} style={{ marginTop: 16, padding: 12, background: 'var(--bg-subtle)', borderRadius: 6, textAlign: 'center', fontSize: 13 }}>
              {twType === 'free'
                ? 'Horário de entrega livre (sem restrição)'
                : `Entrega entre ${String(twStartH).padStart(2, '0')}:${String(twStartM).padStart(2, '0')} e ${String(twEndH).padStart(2, '0')}:${String(twEndM).padStart(2, '0')}`}
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 20, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setTwModalOpen(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={saveTimeWindow}>Aplicar janela</button>
            </div>
          </div>
        </div>

        {saving && (
          <div className="opt-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
            <div className="opt-spinner" />
            <div className="opt-msg" style={{ marginTop: 16, color: 'white', fontWeight: 600 }}>Criando e otimizando rota...</div>
          </div>
        )}
      </div>
    </Layout>
  );
}
